import { useQuery } from "@tanstack/react-query";
import type { Id } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";

/** A variant, labelled for a picker or a lines table — never for invoicing
 * or anything that needs the record itself. */
export interface VariantOption {
  id: Id;
  sku: string | null;
  /** `"Product Title — Variant Title"`, or just the product title for the
   * common single-variant case where the variant title is Shopify's
   * meaningless "Default Title". */
  label: string;
}

interface VariantCatalog {
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  options: VariantOption[];
}

function labelFor(productTitle: string, variantTitle: string): string {
  return variantTitle === "Default Title"
    ? productTitle
    : `${productTitle} — ${variantTitle}`;
}

/**
 * Every variant across the whole catalogue, labelled for a transfer line
 * picker or a receipt form.
 *
 * Built from `inventory.list` rather than `catalog.listProducts` +
 * `catalog.getProduct` per product — `CatalogRepository` has no
 * "list every variant" call, and fetching one product at a time to flatten
 * its variants is a request per product (191 on the real catalogue, not the
 * fixture's 32 — the assumption an earlier version of this hook made and
 * that does not hold once there is a real Shopify store behind it). The
 * `Adjustments` screen (M13-8) already solved naming "every variant" the
 * same way for its own picker, for the same reason: reuse it rather than
 * add a second one.
 */
export function useVariantCatalog(enabled: boolean): VariantCatalog {
  const data = useDataSource();

  const rows = useQuery({
    queryKey: ["warehouses", "variant-catalog"],
    queryFn: () => data.inventory.list({ limit: 200 }),
    enabled,
  });

  const options: VariantOption[] = [
    ...new Map(
      (rows.data?.items ?? []).map((row) => [
        row.variantId,
        {
          id: row.variantId,
          sku: row.variant.sku,
          label: labelFor(row.product.title, row.variant.title),
        },
      ]),
    ).values(),
  ];

  return {
    isPending: rows.isPending,
    isError: rows.isError,
    error: rows.error,
    options,
  };
}
